<template>
  <div class="ohif-viewer-container">
    <!-- Loading overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando visualizador...</span>
        </div>
        <h5 class="mt-3">Carregando OHIF Viewer</h5>
        <p class="text-muted">{{ loadingMessage }}</p>
      </div>
    </div>

    <!-- Error overlay -->
    <div v-if="error" class="error-overlay">
      <div class="error-content">
        <i class="bx bx-error-circle text-danger display-1"></i>
        <h4 class="mt-3 text-danger">Erro ao carregar visualizador</h4>
        <p class="text-muted">{{ error }}</p>
        <div class="mt-4">
          <button class="btn btn-primary me-2" @click="retryLoading">
            <i class="bx bx-refresh me-1"></i>
            Tentar Novamente
          </button>
          <button class="btn btn-outline-secondary" @click="goBack">
            <i class="bx bx-arrow-back me-1"></i>
            Voltar
          </button>
        </div>
      </div>
    </div>

    <!-- Study info header -->
    <div v-if="studyInfo && !loading && !error" class="study-info-header">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-md-8">
            <h6 class="mb-1">
              <i class="bx bx-user me-1"></i>
              {{ studyInfo.patient_name || 'Paciente não identificado' }}
            </h6>
            <small class="text-muted">
              Estudo: {{ study_uid }} | 
              Data: {{ formatDate(studyInfo.study_date) }} |
              Modalidade: {{ studyInfo.modality || 'N/A' }}
            </small>
          </div>
          <div class="col-md-4 text-end">
            <div class="btn-group btn-group-sm">
              <button 
                class="btn btn-outline-primary" 
                @click="fullscreenToggle"
                :title="isFullscreen ? 'Sair do modo tela cheia' : 'Modo tela cheia'"
              >
                <i :class="isFullscreen ? 'bx bx-exit-fullscreen' : 'bx bx-fullscreen'"></i>
              </button>
              <button 
                class="btn btn-outline-secondary" 
                @click="showSettings = !showSettings"
                title="Configurações"
              >
                <i class="bx bx-cog"></i>
              </button>
              <button 
                class="btn btn-outline-danger" 
                @click="closeViewer"
                title="Fechar visualizador"
              >
                <i class="bx bx-x"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings panel -->
    <div v-if="showSettings && !loading && !error" class="settings-panel">
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="bx bx-cog me-1"></i>
            Configurações do Visualizador
          </h6>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label">Qualidade de Imagem</label>
                <select class="form-select form-select-sm" v-model="imageQuality" @change="updateImageQuality">
                  <option value="low">Baixa (Rápida)</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta (Lenta)</option>
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-3">
                <label class="form-label">Orientação Padrão</label>
                <select class="form-select form-select-sm" v-model="defaultOrientation">
                  <option value="axial">Axial</option>
                  <option value="sagittal">Sagital</option>
                  <option value="coronal">Coronal</option>
                </select>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="enableAnnotations" 
                  v-model="enableAnnotations"
                  :disabled="!permissions.canAnnotate"
                >
                <label class="form-check-label" for="enableAnnotations">
                  Habilitar anotações
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="enableMeasurements" 
                  v-model="enableMeasurements"
                  :disabled="!permissions.canMeasure"
                >
                <label class="form-check-label" for="enableMeasurements">
                  Habilitar ferramentas de medição
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- OHIF Viewer container -->
    <div 
      v-show="!loading && !error" 
      id="ohif-viewer" 
      class="ohif-container"
      :class="{ 'fullscreen-mode': isFullscreen }"
    ></div>

    <!-- Floating tools panel (visible only for authorized users) -->
    <div 
      v-if="showFloatingTools && !loading && !error && permissions.canMeasure" 
      class="floating-tools"
    >
      <div class="tools-panel">
        <button 
          class="tool-btn" 
          @click="activateTool('zoom')"
          :class="{ active: activeTool === 'zoom' }"
          title="Zoom"
        >
          <i class="bx bx-search-alt"></i>
        </button>
        <button 
          class="tool-btn" 
          @click="activateTool('pan')"
          :class="{ active: activeTool === 'pan' }"
          title="Mover"
        >
          <i class="bx bx-move"></i>
        </button>
        <button 
          class="tool-btn" 
          @click="activateTool('windowLevel')"
          :class="{ active: activeTool === 'windowLevel' }"
          title="Janela/Nível"
        >
          <i class="bx bx-adjust"></i>
        </button>
        <button 
          class="tool-btn" 
          @click="activateTool('length')"
          :class="{ active: activeTool === 'length' }"
          title="Medição Linear"
        >
          <i class="bx bx-ruler"></i>
        </button>
        <hr class="tool-divider">
        <button 
          class="tool-btn" 
          @click="resetView"
          title="Resetar Vista"
        >
          <i class="bx bx-reset"></i>
        </button>
        <button 
          class="tool-btn" 
          @click="captureScreenshot"
          title="Capturar Tela"
          v-if="permissions.canExport"
        >
          <i class="bx bx-camera"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { OHIFViewer } from '@/components/OHIFViewer';
import ViewerAuth from '@/utils/viewerAuth';
import { dicomService } from '@/api/dicom';

export default {
  name: 'OHIFViewerPage',
  props: {
    study_uid: {
      type: String,
      required: true
    },
    series_uid: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      viewer: null,
      viewerAuth: null,
      loading: true,
      error: null,
      loadingMessage: 'Validando acesso...',
      studyInfo: {},
      permissions: {},
      userRole: null,
      
      // UI State
      isFullscreen: false,
      showSettings: false,
      showFloatingTools: true,
      activeTool: 'windowLevel',
      
      // Settings
      imageQuality: 'medium',
      defaultOrientation: 'axial',
      enableAnnotations: false,
      enableMeasurements: true,
    };
  },
  async created() {
    await this.initializeViewer();
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    async initializeViewer() {
      try {
        this.loadingMessage = 'Validando acesso ao estudo...';
        
        // Inicializar autenticação
        this.viewerAuth = new ViewerAuth();
        const authResult = await this.viewerAuth.validateAccess(this.study_uid, ['canView']);
        
        if (!authResult.success) {
          throw new Error(authResult.error);
        }

        this.permissions = authResult.permissions;
        this.userRole = authResult.userRole;

        this.loadingMessage = 'Carregando informações do estudo...';
        
        // Carregar informações do estudo
        await this.loadStudyInfo();

        this.loadingMessage = 'Configurando visualizador...';
        
        // Configurar OHIF Viewer
        await this.setupOHIFViewer();

        this.loadingMessage = 'Inicializando visualizador...';
        
        // Inicializar viewer
        await this.viewer.initialize(this.study_uid, this.series_uid);

        this.loading = false;
        this.setupEventListeners();
        
      } catch (error) {
        console.error('Error initializing viewer:', error);
        this.error = error.message;
        this.loading = false;
      }
    },

    async loadStudyInfo() {
      try {
        const response = await dicomService.getStudyById(this.study_uid);
        this.studyInfo = response.data;
      } catch (error) {
        console.error('Error loading study info:', error);
        // Continuar mesmo se não conseguir carregar info do estudo
        this.studyInfo = {
          patient_name: 'Informação não disponível',
          study_date: null,
          modality: 'UNKNOWN'
        };
      }
    },

    async setupOHIFViewer() {
      // Configurar OHIF com base nas permissões do usuário
      const config = {
        userRole: this.userRole,
        orthancUrl: process.env.VUE_APP_ORTHANC_URL || 'http://localhost:8042',
        headers: this.viewerAuth.getAuthHeaders(),
        enableAdvancedTools: this.permissions.canAccessAdvancedTools,
        enableAnnotations: this.permissions.canAnnotate,
        enableMeasurements: this.permissions.canMeasure,
        enableExport: this.permissions.canExport,
      };

      this.viewer = new OHIFViewer('ohif-viewer', config);
    },

    setupEventListeners() {
      // Escutar mudanças de ferramenta
      document.addEventListener('ohif-tool-changed', this.handleToolChange);
      
      // Escutar eventos de fullscreen
      document.addEventListener('fullscreenchange', this.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
      
      // Atalhos de teclado
      document.addEventListener('keydown', this.handleKeyboard);
    },

    handleToolChange(event) {
      if (event.detail && event.detail.toolName) {
        this.activeTool = event.detail.toolName;
      }
    },

    handleFullscreenChange() {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement
      );
    },

    handleKeyboard(event) {
      // Implementar atalhos de teclado
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'f':
            event.preventDefault();
            this.fullscreenToggle();
            break;
          case 's':
            event.preventDefault();
            if (this.permissions.canExport) {
              this.captureScreenshot();
            }
            break;
        }
      }

      // Atalhos de ferramentas
      switch (event.key) {
        case 'z':
          this.activateTool('zoom');
          break;
        case 'p':
          this.activateTool('pan');
          break;
        case 'w':
          this.activateTool('windowLevel');
          break;
        case 'l':
          if (this.permissions.canMeasure) {
            this.activateTool('length');
          }
          break;
        case 'r':
          this.resetView();
          break;
      }
    },

    activateTool(toolName) {
      if (this.viewer && this.viewer.isInitialized) {
        // Implementar ativação de ferramenta no OHIF
        this.activeTool = toolName;
        // this.viewer.setActiveTool(toolName);
      }
    },

    resetView() {
      if (this.viewer) {
        this.viewer.resetView();
      }
    },

    fullscreenToggle() {
      const container = document.getElementById('ohif-viewer');
      if (!this.isFullscreen) {
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) {
          container.mozRequestFullScreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        }
      }
    },

    async captureScreenshot() {
      if (this.viewer && this.permissions.canExport) {
        try {
          const screenshot = this.viewer.captureViewport();
          if (screenshot) {
            // Criar download da captura
            const link = document.createElement('a');
            link.download = `estudo_${this.study_uid}_${Date.now()}.png`;
            link.href = screenshot;
            link.click();
            
            this.$toast.success('Captura de tela realizada com sucesso');
          }
        } catch (error) {
          console.error('Error capturing screenshot:', error);
          this.$toast.error('Erro ao capturar tela');
        }
      }
    },

    updateImageQuality() {
      // Implementar mudança de qualidade de imagem
      if (this.viewer) {
        // this.viewer.setImageQuality(this.imageQuality);
      }
    },

    async retryLoading() {
      this.error = null;
      this.loading = true;
      await this.initializeViewer();
    },

    goBack() {
      this.$router.go(-1);
    },

    closeViewer() {
      this.$router.push('/dicom/studies');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('pt-BR');
      } catch {
        return 'Data inválida';
      }
    },

    cleanup() {
      // Remover event listeners
      document.removeEventListener('ohif-tool-changed', this.handleToolChange);
      document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
      document.removeEventListener('keydown', this.handleKeyboard);

      // Destruir viewer
      if (this.viewer) {
        this.viewer.destroy();
      }
    }
  }
};
</script>

<style scoped>
.ohif-viewer-container {
  position: relative;
  width: 100%;
  height: 100vh;
  background-color: #000;
  overflow: hidden;
}

.loading-overlay,
.error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-content,
.error-content {
  text-align: center;
  color: white;
  padding: 2rem;
}

.study-info-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 0;
  z-index: 100;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.settings-panel {
  position: absolute;
  top: 60px;
  right: 1rem;
  width: 400px;
  z-index: 200;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.ohif-container {
  width: 100%;
  height: 100%;
  background-color: #000;
}

.ohif-container.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
}

.floating-tools {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 150;
}

.tools-panel {
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.tool-btn {
  width: 40px;
  height: 40px;
  border: none;
  background-color: transparent;
  color: white;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.tool-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.tool-btn.active {
  background-color: #007bff;
  color: white;
}

.tool-divider {
  margin: 0.25rem 0;
  border-color: rgba(255, 255, 255, 0.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .settings-panel {
    width: calc(100% - 2rem);
    right: 1rem;
    left: 1rem;
  }
  
  .floating-tools {
    bottom: 1rem;
    left: 50%;
    top: auto;
    transform: translateX(-50%);
  }
  
  .tools-panel {
    flex-direction: row;
    width: auto;
  }
}

/* Hide scrollbars but keep functionality */
.settings-panel::-webkit-scrollbar {
  width: 4px;
}

.settings-panel::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.settings-panel::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}
</style>
